import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, ProtectedRoute, PublicRoute } from '@/providers/auth-provider'

// Loading component
const PageLoading = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
)

// Lazy load pages
const LandingPage = lazy(() => import('@/pages/LandingPage'))
const LoginPage = lazy(() => import('@/pages/LoginPage'))
const VerifyEmailPage = lazy(() => import('@/pages/VerifyEmailPage'))
const DashboardLayout = lazy(() => import('@/components/layout/DashboardLayout'))
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage'))
const ClientsPage = lazy(() => import('@/pages/clients/ClientsPage'))
const ClientDetailPage = lazy(() => import('@/pages/clients/ClientDetailPage'))
const ProjectsPage = lazy(() => import('@/pages/projects/ProjectsPage'))
const ProjectsTrashPage = lazy(() => import('@/pages/projects/ProjectsTrashPage'))
const ProjectNewPage = lazy(() => import('@/pages/projects/ProjectNewPage'))
const ProjectDetailPage = lazy(() => import('@/pages/projects/ProjectDetailPage'))
const ProjectSettingsPage = lazy(() => import('@/pages/projects/ProjectSettingsPage'))
const SystemInstancesPage = lazy(() => import('@/pages/system-instances/SystemInstancesPage'))
const SystemInstanceDetailPage = lazy(() => import('@/pages/system-instances/SystemInstanceDetailPage'))
const AtomPage = lazy(() => import('@/pages/atom/AtomPage'))
const AtomDetailPage = lazy(() => import('@/pages/atom/AtomDetailPage'))
const IndustriesPage = lazy(() => import('@/pages/industries/IndustriesPage'))
const IndustryDetailPage = lazy(() => import('@/pages/industries/IndustryDetailPage'))
const ApiCatalogPage = lazy(() => import('@/pages/api-catalog/ApiCatalogPage'))
const ApiCatalogDetailPage = lazy(() => import('@/pages/api-catalog/ApiCatalogDetailPage'))
const CompanyProfilePage = lazy(() => import('@/pages/settings/CompanyProfilePage'))
const NLProjectBuilderPage = lazy(() => import('@/pages/nl-project-builder/NLProjectBuilderPage'))
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'))

export default function App() {
  return (
    <AuthProvider>
      <Suspense fallback={<PageLoading />}>
        <Routes>
          {/* Public routes */}
          <Route
            path="/"
            element={
              <PublicRoute>
                <LandingPage />
              </PublicRoute>
            }
          />
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />
          <Route
            path="/verify-email"
            element={
              <PublicRoute>
                <VerifyEmailPage />
              </PublicRoute>
            }
          />

          {/* Protected routes with DashboardLayout */}
          <Route
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={<DashboardPage />} />

            {/* Clients */}
            <Route path="/clients" element={<ClientsPage />} />
            <Route path="/clients/:id" element={<ClientDetailPage />} />

            {/* Projects */}
            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/projects/trash" element={<ProjectsTrashPage />} />
            <Route path="/projects/new" element={<ProjectNewPage />} />
            <Route path="/projects/:id" element={<ProjectDetailPage />} />
            <Route path="/projects/:id/settings" element={<ProjectSettingsPage />} />
            <Route path="/system-instances" element={<SystemInstancesPage />} />
            <Route path="/system-instances/:id" element={<SystemInstanceDetailPage />} />

            {/* Atoms */}
            <Route path="/atoms" element={<AtomPage />} />
            <Route path="/atoms/:id" element={<AtomDetailPage />} />

            {/* Industries */}
            <Route path="/industries" element={<IndustriesPage />} />
            <Route path="/industries/:id" element={<IndustryDetailPage />} />

            {/* Development */}
            <Route path="/development/api-catalog" element={<ApiCatalogPage />} />
            <Route path="/development/api-catalog/:id" element={<ApiCatalogDetailPage />} />
            <Route path="/development/nl-project-builder" element={<NLProjectBuilderPage />} />

            {/* Settings */}
            <Route path="/settings/company-profile" element={<CompanyProfilePage />} />
          </Route>

          {/* Redirects */}
          <Route path="/signup" element={<Navigate to="/login" replace />} />
          <Route path="/forgot-password" element={<Navigate to="/login" replace />} />

          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </AuthProvider>
  )
}
